import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';
import boardCache from './BoardCache';
import boardItemCache from './BoardItemCache';
import boardFetcher from './BoardFetcher';
import boardLaneBuilder from './BoardLaneBuilder';
import boardSaver from './BoardSaver';
import boardDropResolver from './BoardDropResolver';
import { BOARD_FORMAT } from './boardConst';

const _name = 'BoardEngine';

// Boards (componentboard_data notes) + their items via parentRK, cache updated from OnJournalUpdated (no refetch)
class BoardEngine extends WssEventHandlerBase {
    _name = _name;
    _loaded = new Set();

    // no this.clear() here — clear() empties the caches and would wipe the boot-fetch
    bootup() {
        super.bootup();
        if (this._unsub) this._unsub();
        this._unsub = this._wsNotifier.subscribe(events.onJournalUpdated, this.onJournalUpdated);
    }

    clear() {
        super.clear();
        if (this._unsub) this._unsub();
        this._unsub = null;
        this._loaded.clear();
        boardCache.clear();
        boardItemCache.clear();
    }

    /**
     * Applies pushed journals to the caches of loaded boards, then notifies the touched boards.
     * @param {object[]} journals - updated journal entries (full objects incl. body)
     */
    onJournalUpdated = (journals) => {
        try {
            if (!Array.isArray(journals)) return;

            const touched = new Set();
            journals.forEach((j) => this._applyJournal(j, touched));
            touched.forEach((rk) => this._notify(rk));
        } catch (error) {
            this.logError('onJournalUpdated', error.message);
        }
    };

    // board note → board cache, everything else → item cache of a loaded board
    _applyJournal = (j, touched) => {
        if (j.format === BOARD_FORMAT) {
            this._applyBoard(j);
            touched.add(j.rk);
            return;
        }

        const oldParent = boardItemCache.getKey(j.rk)?.parentRK;
        const keep = boardFetcher.isOnBoard(j) && this._loaded.has(j.parentRK);

        if (keep) boardItemCache.setKey(j.rk, boardFetcher.mapItem(j));
        else if (oldParent) boardItemCache.remove(j.rk);

        [j.parentRK, oldParent].filter((rk) => rk && this._loaded.has(rk)).forEach((rk) => touched.add(rk));
    };

    _applyBoard = (j) => {
        if (boardFetcher.isActive(j)) boardCache.setKey(j.rk, boardFetcher.mapBoard(j));
        else boardCache.remove(j.rk);
    };

    fetchBoards = async () => {
        try {
            const boards = await boardFetcher.fetchBoards();
            boardCache.clear();
            boards.forEach((b) => boardCache.setKey(b.rk, b));
            this._notify(null);
            return boards;
        } catch (error) {
            this.logError('fetchBoards', error.message);
            return [];
        }
    };

    /**
     * @param {string} boardRk
     * @returns {Promise<object[]>}
     */
    fetchItems = async (boardRk) => {
        try {
            this._loaded.add(boardRk);
            const items = await boardFetcher.fetchItems(boardRk);
            boardItemCache.replaceForParent(boardRk, items);
            this._notify(boardRk);
            return items;
        } catch (error) {
            this.logError('fetchItems', error.message);
            return [];
        }
    };

    getBoards = () => boardCache.getAll();

    getBoard = (rk) => boardCache.getKey(rk);

    findBoard = (title) => boardCache.findByTitle(title);

    getItem = (rk) => boardItemCache.getKey(rk);

    /**
     * @param {string} boardRk
     * @param {{filter:string, sorts:object}} opts
     * @returns {object|null} lane model, null when the board is not loaded
     */
    getView = (boardRk, opts) => {
        const board = boardCache.getKey(boardRk);
        if (!board) return null;
        return boardLaneBuilder.build(board, boardItemCache.getByParent(boardRk), opts);
    };

    /**
     * Turns a lane on/off and saves the board — optimistic, reverted when the save fails.
     * @param {string} boardRk
     * @param {string} laneId
     * @param {boolean} on
     * @returns {Promise<boolean>}
     */
    setLaneEnabled = async (boardRk, laneId, on) => {
        const board = boardCache.getKey(boardRk);
        if (!board) return false;
        const next = { ...board, lanes: board.lanes.map((l) => (l.id === laneId ? { ...l, enabled: on } : l)) };
        boardCache.setKey(boardRk, next);
        this._notify(boardRk);

        const ok = await boardSaver.saveLanes(next);
        if (!ok) {
            boardCache.setKey(boardRk, board);
            this._notify(boardRk);
        }
        return ok;
    };

    /**
     * Quick-add: new Issue in a stage lane, appears via the OnJournalUpdated push.
     * @param {string} boardRk
     * @param {string} laneId
     * @param {string} title
     * @param {string} cat
     * @returns {Promise<boolean>}
     */
    addIssue = (boardRk, laneId, title, cat) => {
        const board = boardCache.getKey(boardRk);
        const lane = board?.lanes.find((l) => l.id === laneId);
        if (!lane?.dropStage || !title.trim()) return Promise.resolve(false);

        return boardSaver.createIssue(board, lane.dropStage, title.trim(), cat);
    };

    /**
     * Optimistic stage change — the OnJournalUpdated push confirms or corrects it.
     * @param {string} itemRk
     * @param {string} stage
     */
    setStage = async (itemRk, stage) => {
        const item = boardItemCache.getKey(itemRk);
        if (!item || item.stage === stage) return;

        boardItemCache.setKey(itemRk, { ...item, stage });
        this._notify(item.parentRK);
        await boardSaver.saveStage(item, stage);
    };

    /**
     * Drop on any lane — optimistic, the OnJournalUpdated push confirms or corrects it.
     * @param {string} itemRk
     * @param {string} boardRk
     * @param {string} laneId
     */
    moveToLane = async (itemRk, boardRk, laneId) => {
        const item = boardItemCache.getKey(itemRk);
        const board = boardCache.getKey(boardRk);
        const lane = board?.lanes.find((l) => l.id === laneId);
        if (!item || !lane) return;

        const fields = boardDropResolver.resolve(item, lane, board.lanes);
        boardItemCache.setKey(itemRk, { ...item, ...fields });
        this._notify(item.parentRK);
        await boardSaver.saveFields(item, fields);
    };

    /**
     * Optimistic title change — the OnJournalUpdated push confirms or corrects it.
     * @param {string} itemRk
     * @param {string} title
     */
    setTitle = async (itemRk, title) => {
        const item = boardItemCache.getKey(itemRk);
        if (!item || item.title === title) return;

        boardItemCache.setKey(itemRk, { ...item, title });
        this._notify(item.parentRK);
        await boardSaver.saveTitle(item, title);
    };

    // notify carries only the rk — widget reads back from the engine
    _notify = (rk) => {
        ComponentNotifier.notify(_name, 'dataChanged', { rk });
    };
}

const boardEngine = new BoardEngine();
export default boardEngine;
